import urls from "global/constants/UrlConstants";
import { detectFrameOrigin } from "./embedBridge";
import {
  getEmbedCallParams,
  getEmbedFileCallParams,
  getNoAuthCallParams,
  makeCall,
} from "utils/service";

export interface EmbedExchangeResponse {
  scope: "signing" | "creation";
  requestId: string;
  requestToken?: string;
  key?: string;
  sessionSecret?: string;
  parentOrigin: string;
  expiresInSeconds: number;
}

export interface EmbedEditorBundle {
  request: any;
  theme?: { primaryColor?: string; buttonTextColor?: string };
  features: any[];
  customFields: any[];
  expiresInSeconds: number;
  /** The branding the signers' emails carry, for the Step 4 preview; absent when none is enabled. */
  emailBranding?: any;
  /** Identity-verification methods a vendor runs here, for the signer security menu. */
  idvMethods?: string[];
}

/**
 * Redeems the one-time token minted by the partner backend. The page that embeds this iframe is sent with
 * it (read from the browser, see detectFrameOrigin); the server redeems the token only for the origin it
 * was minted for and still has registered.
 */
export const exchangeEmbedToken = async (
  token: string,
): Promise<EmbedExchangeResponse> => {
  return makeCall(
    urls.EMBED_EXCHANGE,
    getNoAuthCallParams("POST", { token, frameOrigin: detectFrameOrigin() }),
  );
};

/** Records sign_started once per signer (the server de-duplicates); never blocks signing. */
export const reportEmbedSignStarted = async (
  requestToken: string,
  key: string,
) => {
  const url = `${urls.EMBED_EVENT}/${encodeURIComponent(requestToken)}?id=${encodeURIComponent(
    requestToken,
  )}&key=${encodeURIComponent(key)}`;
  return makeCall(url, getNoAuthCallParams("POST", { event: "sign_started" }));
};

export const fetchEmbedEditor = async (
  requestId: string,
): Promise<EmbedEditorBundle> => {
  return makeCall(
    `${urls.EMBED_EDITOR}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("GET"),
  );
};

export const fetchEmbedDocumentUrl = async (requestId: string, file: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_DOCUMENT_URL}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", file),
  );
};

export const fetchEmbedDimensions = async (requestId: string, file: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_DIMENSIONS}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", file),
  );
};

/** AI summary / contract-at-a-glance of one of the draft's documents. Same body and response as the app's call. */
export const fetchEmbedDocumentSummary = async (requestId: string, file: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_AI_SUMMARY}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", file),
    true,
    // AI extraction can take well over the 30s default, as in the app.
    { timeoutMs: 120000 },
  );
};

/** AI field detection on one of the draft's documents. Same body and response as the app's detectFields. */
export const detectEmbedFields = async (requestId: string, unsignedPdf: string) => {
  return makeCall(
    `${urls.EMBED_EDITOR_AI_FIELDS}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", { unsignedPdf }),
  );
};

/** Ask the AI about the draft's documents. Same payload and response as the app's askPdf. */
export const askEmbedPdf = async (requestId: string, payload: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_AI_CHAT}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", payload),
  );
};

/** The sender's SMS consent attestation for the draft's recipients. Same body as the app's call. */
export const recordEmbedSmsConsent = async (requestId: string, body: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_SMS_CONSENT}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", body),
  );
};

/** How many documents the draft may hold. */
export const fetchEmbedDocumentLimit = async (
  requestId: string,
): Promise<number> => {
  const res = await makeCall(
    `${urls.EMBED_EDITOR_DOCUMENT_LIMIT}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("GET"),
  );
  return Number(res?.limit) || 0;
};

/** Adds one document to the draft; answers the stored file (name, url, any fields found in it). */
export const uploadEmbedDocument = async (
  requestId: string,
  formData: FormData,
  signal?: AbortSignal,
) => {
  return makeCall(
    `${urls.EMBED_EDITOR_DOCUMENT_UPLOAD}/${encodeURIComponent(requestId)}`,
    getEmbedFileCallParams("POST", formData, signal),
  );
};

export const removeEmbedDocument = async (requestId: string, file: any) => {
  return makeCall(
    `${urls.EMBED_EDITOR_DOCUMENT_REMOVE}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", file),
  );
};

export interface EmbedDraftUpdate {
  emailSubject?: string;
  emailMessage?: string;
  expirationDays?: number;
  needReminders?: boolean;
  reminders?: any;
  signerOrderRequired?: boolean;
  signers?: any[];
  files?: { unsignedPdf: string; fields: any[] }[];
  /**
   * This wizard offers the full Step 2 / Step 4 settings: the server then takes the approval, the schedule
   * and the signer permissions from this body. Delivery modes are always the server's own rule.
   */
  explicitSettings?: boolean;
  approval?: any;
  /** "MM/DD/YYYY HH:mm:ss" in the backend's zone, or null to send now. */
  scheduleTime?: string | null;
  signRequestConfig?: any;
}

/** Saves the wizard's signers, email settings and field placements on the draft. */
export const saveEmbedDraft = async (
  requestId: string,
  update: EmbedDraftUpdate,
) => {
  return makeCall(
    `${urls.EMBED_EDITOR_DRAFT}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", update),
  );
};

/** Sends the draft with field placements only: [{ unsignedPdf, fields }]. */
export const sendEmbedDraft = async (
  requestId: string,
  files: { unsignedPdf: string; fields: any[] }[],
) => {
  return makeCall(
    `${urls.EMBED_EDITOR_SEND}/${encodeURIComponent(requestId)}`,
    getEmbedCallParams("POST", { files }),
  );
};
